/**
 * Phase 17 (+ 19, 40) — what the formula and the gateway rules do to the pool
 * on screen, through the SAME shared rules the engine uses: the rotation
 * order (ranked best first, then the unranked as dragged), the routes a
 * "leave the pool" rule took out (every route out → the pool stays, as the
 * engine does), the "last resort" routes set aside, and the shares (each
 * ranked gateway's share of the positive scores, the unranked at the mean
 * ranked share; a route out or set aside draws nothing). The tracker ranks
 * GATEWAYS; a pool row is a LANE (Phase 40): a gateway's lanes sit together
 * in its rank and split its formula share between them. Before the ranking
 * has answered: the pool's own order and equal shares — what the day falls
 * back to when the tracker cannot be read.
 * For Media Devoted | Created By Ciro Autiero
 */
import { type DropGenGatewayRankingPreview, gatewayFormulaOrder, gatewayFormulaWeights, gatewayRuleOutcome, hasActingRules } from '@trafficpulse/shared';

import type { DraftGateway } from '../../../../drop-generator.types';

/** Pool row (lane) indexes: the rotation, the last-resort reserve, and those a rule took out. */
export interface RotationView {
  order: number[];
  reserve: number[];
  left: number[];
}

type Preview = DropGenGatewayRankingPreview | undefined;

/** The pool's gateways once each, in first-lane order — what the tracker is asked about. */
export const poolRouteIds = (gw: Pick<DraftGateway, 'pool'>): number[] => [...new Set(gw.pool.map((r) => r.routeId))];

/** The pool indexes of a gateway's lanes, in pool order. */
const lanesOf = (gw: DraftGateway, routeId: number): number[] => gw.pool.flatMap((l, i) => (l.routeId === routeId ? [i] : []));

/** Rules that act on the day (leave the pool · last resort) — "Not enough data" alone only matters to the formula. */
export const rulesInUse = (gw: DraftGateway): boolean => hasActingRules(gw.ranking.rules);

function ruleSets(gw: DraftGateway, preview: Preview) {
  if (!preview || !rulesInUse(gw)) return { left: new Set<number>(), last: new Set<number>() };
  const o = gatewayRuleOutcome(preview.routes);
  return { left: new Set(o.left.map((l) => l.routeId)), last: new Set(o.lastResort) };
}

export function rotationOrder(gw: DraftGateway, preview: Preview): RotationView {
  const route = (i: number) => gw.pool[i].routeId;
  const lanes = gw.orderMode === 'FORMULA' && preview ? gatewayFormulaOrder(poolRouteIds(gw), preview.routes).flatMap((id) => lanesOf(gw, id)) : gw.pool.map((_, i) => i);
  const { left, last } = ruleSets(gw, preview);
  const kept = lanes.filter((i) => !left.has(route(i)));
  const live = kept.length > 0 ? kept : lanes;
  const normal = live.filter((i) => !last.has(route(i)));
  return {
    order: normal.length > 0 ? normal : live,
    reserve: normal.length > 0 ? live.filter((i) => last.has(route(i))) : [],
    left: kept.length > 0 ? lanes.filter((i) => left.has(route(i))) : [],
  };
}

/** One share per pool row (any scale) — what the bar and the split dots draw. A gateway's formula share is split over its lanes. */
export function poolShares(gw: DraftGateway, preview: Preview): number[] {
  let weights = gw.pool.map(() => 1);
  if (gw.weightsMode === 'MANUAL') weights = gw.pool.map((r) => Math.max(0, r.weight ?? 0));
  if (gw.weightsMode === 'FORMULA' && preview) {
    const shares = gatewayFormulaWeights(poolRouteIds(gw), preview.routes);
    weights = gw.pool.map((l) => (shares.get(l.routeId) ?? 0) / lanesOf(gw, l.routeId).length);
  }
  const active = new Set(rotationOrder(gw, preview).order);
  const drawn = weights.map((w, i) => (active.has(i) ? w : 0));
  return drawn.some((w) => w > 0) ? drawn : gw.pool.map((_, i) => (active.has(i) ? 1 : 0));
}

/** Is the formula in use for this strategy (order for Hold/Alternate, weights for Random/Split)? */
export function formulaInUse(gw: DraftGateway): boolean {
  const weighted = gw.strategy === 'RANDOM' || gw.strategy === 'SPLIT';
  return weighted ? gw.weightsMode === 'FORMULA' : gw.orderMode === 'FORMULA';
}

/** The tracker is read for the formula, or for an enabled rule (the engine's rule). */
export const rankingNeeded = (gw: DraftGateway): boolean => formulaInUse(gw) || rulesInUse(gw);
